// PaymentPage.tsx
import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {getPaymentInfo} from '../../api/transaction/payApi';
import {toggleFavorite} from '../../api/coordiApi';
import {setComma} from "../../utils/format";
import {defaultAlert, defaultConfirm} from "../../utils/alert";
import {Loading} from "../../components/common/Loading";
import defaultMaleProfileImg from '../../assets/images/man_default_img.png';
import defaultFemaleProfileImg from '../../assets/images/woman_default_img.png';

export default function PaymentPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const jobPostingCode = searchParams.get('jobPostingCode');
    const coordinatorCode = searchParams.get('coordinatorCode');

    const [jobData, setJobData] = useState<any>(null);
    const [coordiData, setCoordiData] = useState<any>(null);
    const [balance, setBalance] = useState(0);
    const [paymentFeeRate, setPaymentFeeRate] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('WINPAY');
    const [isAgreed, setIsAgreed] = useState(false);

    useEffect(() => {
        fetchData();
    }, [jobPostingCode, coordinatorCode]);

    const fetchData = async () => {
        try {
            const response = await getPaymentInfo(jobPostingCode || '', coordinatorCode || '');
            const job = response.data.jobData;

            // 공고 상태가 모집중(RECRUITING)이 아니면 결제 불가
            if (job?.status && job.status !== 'RECRUITING') {
                defaultAlert('모집중인 공고만 결제를 진행할 수 있습니다.');
                navigate(-1);
                return;
            }

            setJobData(job);
            setCoordiData(response.data.coordiData);
            setBalance(response.data.balance);
            setPaymentFeeRate(Number(response.data.paymentFeeRate || 0));
            setIsFavorite(response.data.coordiData.favorite);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            defaultAlert('정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };
    const handleToggleFavorite = async () => {
        try {
            const response = await toggleFavorite(coordinatorCode || '');
            setIsFavorite(response.data);
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };


    const getHoursPerDay = (): number => {
        if (!jobData?.workStartTime || !jobData?.workEndTime) return 0;
        const [startH, startM] = jobData.workStartTime.split(':').map(Number);
        const [endH, endM] = jobData.workEndTime.split(':').map(Number);
        return (endH * 60 + endM - (startH * 60 + startM)) / 60;
    };

    const calculateBaseAmount = () => {
        if (!coordiData || !jobData) return 0;
        if (jobData.workType === 'HOURLY') {
            // 시간제: 시급 × 하루 근무시간 × 총 일수
            return coordiData.amount * getHoursPerDay() * jobData.totalDays;
        }
        // 종일제: 일급 × 총 일수
        return coordiData.amount * jobData.totalDays;
    };

    const calculatePaymentFee = (baseAmount: number) => {
        if (!paymentFeeRate) return 0;
        return Math.floor((baseAmount * paymentFeeRate) / 100);
    };

    const calculateTotalAmount = () => {
        const base = calculateBaseAmount();
        return base + calculatePaymentFee(base);
    };

    const handlePayment = async () => {
        if (jobData?.status && jobData.status !== 'RECRUITING') {
            defaultAlert('모집중인 공고만 결제를 진행할 수 있습니다.');
            return;
        }
        if (!isAgreed) {
            defaultAlert('결제 진행에 동의해주세요.');
            return;
        }
        if (!jobPostingCode || !coordinatorCode) {
            defaultAlert('잘못된 접근입니다.');
            return;
        }
        const totalAmount = calculateTotalAmount();
        if (balance < totalAmount) {
            defaultConfirm('잔액이 부족합니다. 충전하시겠습니까?', '충전하기', '취소', (isConfirmed) => {
                if (isConfirmed) {
                    sessionStorage.setItem('chargeReturnPath', `/payment?jobPostingCode=${jobPostingCode}&coordinatorCode=${coordinatorCode}`);
                    navigate('/wallet/charge');
                }
            });
            return;
        }

        // 비밀번호 페이지로 이동하면서 성공 시 실행할 콜백 함수 전달
        navigate('/payment-password', {
            state: {
                title: '결제 비밀번호 입력',
                subtitle: '결제를 진행하려면 비밀번호를 입력해주세요.',
                returnPath: '/announcement/matching',
                paymentData: {
                    jobPostingCode: jobPostingCode,
                    coordinatorCode: coordinatorCode,
                    amount: totalAmount,
                    paymentMethod: paymentMethod
                }
            }
        });
    };


    if (!jobData || !coordiData) {
        return <div>정보를 찾을 수 없습니다.</div>;
    }

    const baseAmount = calculateBaseAmount();
    const paymentFee = calculatePaymentFee(baseAmount);
    const totalAmount = calculateTotalAmount();

    return (
        <>
            <Loading loading={loading} onClose={() => setLoading(false)}/>
            <div>
                {/* 하단 버튼 */}
                <div className="bottom-btn">
                    <button
                        type="button"
                        className="btn confirm"
                        onClick={handlePayment}
                    >
                        결제하기
                    </button>
                </div>

                {/* 컨테이너 */}
                <main className="container type_gray">
                    {/* 코디 정보 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">코디 정보</span>
                            </h3>
                            <div className="round-box mt10">
                                <div className="round-body">
                                    <div className="coordi-profile-box">
                                        <div className="coordi-info">
                                            <p className="name">
                                                {coordiData.memberName}
                                                <span className="career">
    {coordiData.hasCareer
        ? `경력 ${coordiData.careerYear}년 ${coordiData.careerMonth}개월`
        : '경력없음'
    }
</span>
                                            </p>
                                            <p className="pay">
                                                <span className="badge">{jobData.workType === 'HOURLY' ? '시급' : '일급'}</span>{setComma(coordiData.amount)}원
                                            </p>
                                        </div>
                                        <span className="like-check">
                                        <input
                                            type="checkbox"
                                            id="favorites1"
                                            className="blind"
                                            checked={isFavorite}
                                            onChange={handleToggleFavorite}
                                        />
                                        <label htmlFor="favorites1"></label>
                                    </span>
                                        <div className="profile-img">
                                            <div className="thumbnail-wrap">
                                                <div className="thumbnail">
                                                    <div className="centered">
                                                        <img
                                                            src={coordiData.imagePath && coordiData.imagePath !== 'default' ? coordiData.imagePath : (coordiData.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg)}
                                                            className="portrait"
                                                            alt="프로필"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).src = coordiData.gender === '여성' ? defaultFemaleProfileImg : defaultMaleProfileImg;
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="coordi-cate">
                                        <div className="ico-info">
                                            <p className="tit">나이</p>
                                            <p className="cont">
                                                <i className="fa-regular fa-file-lines"></i>{coordiData.age}
                                            </p>
                                        </div>
                                        <div className="ico-info line">
                                            <p className="tit">성별</p>
                                            <p className="cont">
                                                <i className={`fa-solid ${coordiData.gender === '남성' ? 'fa-mars' : 'fa-venus'}`}></i>{coordiData.gender}
                                            </p>
                                        </div>
                                        <div className="ico-info line">
                                            <p className="tit">국적</p>
                                            <p className="cont">{coordiData.nationalityName}</p>
                                        </div>
                                    </div>
                                    <ul className="career-tit pbNone">
                                        <li>
                                        <span className="tit-cont complete">
                                            <i className="fa-solid fa-circle-check"></i>완료 <strong>{coordiData.completed || 0}건</strong>
                                        </span>
                                        </li>
                                        <li>
                                        <span className="tit-cont award">
                                            <i className="fa-solid fa-award"></i>자격증 <strong>{coordiData.certificates.length || 0}개</strong>
                                        </span>
                                        </li>
                                        <li>
                                        <span className="tit-cont service">
                                            <i className="fa-solid fa-thumbs-up"></i>서비스 후기 <strong>{coordiData.reviewCount || 0}건</strong>
                                        </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 공고 정보 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">공고 정보</span>
                            </h3>
                            <div className="round-box mt10">
                                <div className="round-body">
                                    <ul className="detail-info col2">
                                        <li className="full">
                                            <span className="tit">공고번호</span>
                                            <span className="txt">{jobData.jobPostingCode}</span>
                                        </li>
                                        <li>
                                            <span className="tit">환자이름</span>
                                            <span className="txt">{jobData.patientName}</span>
                                        </li>
                                        <li>
                                            <span className="tit">나이</span>
                                            <span className="txt">{jobData.age}세</span>
                                        </li>
                                        <li>
                                            <span className="tit">성별</span>
                                            <span className="txt">{jobData.gender === 'M' ? '남성' : '여성'}</span>
                                        </li>
                                        <li>
                                            <span className="tit">체중(kg)</span>
                                            <span className="txt">{jobData.weight}kg</span>
                                        </li>
                                        <li>
                                            <span className="tit">키(cm)</span>
                                            <span className="txt">{jobData.height}cm</span>
                                        </li>
                                        <li>
                                            <span className="tit">관계</span>
                                            <span className="txt">{jobData.relationshipName}</span>
                                        </li>
                                        <li className="full">
                                            <span className="tit">돌봄 시작일</span>
                                            <span className="txt">{jobData.careStartDate}</span>
                                        </li>
                                        <li className="full">
                                            <span className="tit">돌봄 종료일</span>
                                            <span className="txt">{jobData.careEndDate}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 돌봄 비용 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">돌봄 비용</span>
                            </h3>
                            <div className="round-box mt10">
                                <div className="round-body">
                                    <div className="info-row">
                                        <span className="label">기준단가</span>
                                        <span className="cont">{jobData.workType === 'HOURLY' ? '시급' : '일급'} / {setComma(coordiData.amount)}원</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">기간</span>
                                        <span className="cont">
                                            {jobData.totalDays}일
                                            {jobData.workType === 'HOURLY' && getHoursPerDay() > 0 &&
                                                ` (총 ${jobData.totalDays * getHoursPerDay()}시간)`}
                                        </span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">돌봄 비용</span>
                                        <span className="cont">{baseAmount.toLocaleString()}원</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="label">결제 수수료 ({paymentFeeRate}%)</span>
                                        <span className="cont">{paymentFee.toLocaleString()}원</span>
                                    </div>
                                    <div className="info-row top-line">
                                    <span className="label">
                                        <strong>총 결제금액</strong>
                                    </span>
                                        <span className="cont">
                                        <strong>{totalAmount.toLocaleString()}원</strong>
                                    </span>
                                    </div>
                                    <div className="info-row foot">
                                        <div className="guide-box mt10 yellow">
                                            <h5 className="point-tit">위약금 안내</h5>
                                            <ul className="guidF-list">
                                                <li>매칭 결제 예약 후 서비스 전 취소 시 위약금이 발생할 수 있습니다.</li>
                                                <li>위약금 발생 시 결제금액에서 차감 후 환불이 진행됩니다.</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 결제수단 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">결제수단</span>
                            </h3>
                            <span className="circle-radio-box">
                            <input
                                type="radio"
                                id="rad1"
                                name="paymentMethod"
                                className="blind"
                                value="WINPAY"
                                checked={paymentMethod === 'WINPAY'}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            />
                            <label htmlFor="rad1">WINPAY</label>
                        </span>
                            <div className="pay-balance emoneyType mt10">
                                <span className="tit">WINPAY 잔액</span>
                                <span className="txt">{balance.toLocaleString()}원</span>
                            </div>
                        </div>
                    </section>

                    {/* 결제금액 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <h3 className="cont-tit">
                                <span className="underline">결제금액</span>
                            </h3>
                            <div className="round-box mt10">
                                <div className="round-body">
                                    <div className="info-row">
                                        <span className="label">결제금액</span>
                                        <span className="cont big-number">{totalAmount.toLocaleString()}원</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 동의 체크박스 */}
                    <section className="bg-white">
                        <div className="inner-box">
                            <div className="default-checkbox">
                                <input
                                    type="checkbox"
                                    id="chk1"
                                    name="chk1"
                                    className="input"
                                    checked={isAgreed}
                                    onChange={(e) => setIsAgreed(e.target.checked)}
                                />
                                <label htmlFor="chk1">
                                    위 거래 정보에 대해 충분히 확인하였으며 결제 진행에 동의합니다.
                                </label>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </>
    );
}